import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useTrendAnalysis from '../../hooks/useTrendAnalysis';
import { useDealershipSettings } from '../../contexts/settingsContext';
import {
    PRESETS, SALES_YOY_TIP, aggregateVariantRow, computeEffectiveKpis, filterRiskVariants, formatCurrency, formatSignalValue,
    lastCompleteYear as lastCompleteYearOf, quarterLabels, riskKpis, rollingTrend, togglePreset as nextPresets,
    totalTrend as totalTrendOf,
} from '../../utils/categoryTrends';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend
);

/* ─── Tooltip (CSS hover) ────────────────────────────────────────────────── */
function Tooltip({ text, children }) {
    return (
        <span className="ct-tooltip-wrap">
            {children}
            <svg className="ct-tooltip-icon" viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 2.5a1 1 0 110 2 1 1 0 010-2zM9.5 11a.5.5 0 01-.5.5H7a.5.5 0 010-1h.5V8H7a.5.5 0 010-1h1a.5.5 0 01.5.5v3h.5a.5.5 0 01.5.5z"/>
            </svg>
            <span className="ct-tooltip-box">{text}</span>
        </span>
    );
}

/* ─── Sparkline ─────────────────────────────────────────────────────────── */
function Sparkline({ data, width = 100, height = 24 }) {
    if (!data || data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`);
    return (
        <svg width={width} height={height} className="ct-sparkline">
            <polyline points={pts.join(' ')} fill="none" stroke="var(--text-muted)" strokeWidth="1.5" />
            <circle cx={pts[pts.length - 1].split(',')[0]} cy={pts[pts.length - 1].split(',')[1]} r="2" fill="var(--sag-green)" />
        </svg>
    );
}

/* ─── Delta badge ───────────────────────────────────────────────────────── */
function Delta({ value, suffix = '%', invert = false }) {
    if (value == null) return <span className="ct-delta ct-delta-na">—</span>;
    const good = invert ? value < -5 : value > 5;
    const bad = invert ? value > 5 : value < -5;
    const cls = bad ? 'ct-delta-bad' : good ? 'ct-delta-good' : 'ct-delta-flat';
    const sign = value > 0 ? '+' : '';
    return <span className={`ct-delta ${cls}`}>{sign}{Math.round(value)}{suffix}</span>;
}

/* The category → market signal map, the presets and the currency format
   live in utils/categoryTrends.js, shared with the v2 Branches page. */

/* ─── Trend Badge ────────────────────────────────────────────────────────── */
function TrendBadge({ trend }) {
    if (!trend) return null;
    const isUp = trend === 'Uptrend';
    const isFlat = trend === 'Flat';
    const icon = isUp ? '▲' : isFlat ? '➖' : '▼';
    const cls = isUp ? 'ct-trend-up' : isFlat ? 'ct-trend-flat' : 'ct-trend-down';
    return (
        <span className={`ct-trend-badge ${cls}`}>
            {icon} {trend}
        </span>
    );
}

/* ─── KPI Strip (Trend tab) ──────────────────────────────────────────────── */
function TrendKpiStrip({ kpis, selectedVariant }) {
    if (!kpis) return null;
    const effectiveKpis = kpis;

    const salesYoyCls = (effectiveKpis.sales_yoy_pct ?? 100) < 100 ? 'ct-kpi-v2-red' : 'ct-kpi-v2-green';
    const salesYoyIcon = (effectiveKpis.sales_yoy_pct ?? 100) < 100 ? '▼' : '▲';
    const trendCls = effectiveKpis.market_trend === 'Uptrend' ? 'ct-kpi-v2-green' : effectiveKpis.market_trend === 'Downtrend' ? 'ct-kpi-v2-red' : 'ct-kpi-v2-muted';
    const trendIcon = effectiveKpis.market_trend === 'Uptrend' ? '⬆' : '⬇';
    const variantLabel = selectedVariant ? ` (${selectedVariant})` : '';

    return (
        <div className="ct-kpi-strip-v2">
            <div className="ct-kpi-v2">
                <span className={`ct-kpi-v2-value ${salesYoyCls}`}>
                    {salesYoyIcon} {effectiveKpis.sales_yoy_pct != null ? effectiveKpis.sales_yoy_pct.toFixed(1) : '—'}%
                </span>
                <span className="ct-kpi-v2-label"><Tooltip text={SALES_YOY_TIP}>Sales YoY</Tooltip></span>
            </div>
            <div className="ct-kpi-v2">
                <span className="ct-kpi-v2-value">{formatCurrency(effectiveKpis.sales_tm)}</span>
                <span className="ct-kpi-v2-label"><Tooltip text="Total sold value in the most recent calendar month">Sales TM</Tooltip></span>
            </div>
            <div className="ct-kpi-v2">
                <span className={`ct-kpi-v2-value ${trendCls}`}>
                    {trendIcon} {effectiveKpis.market_trend || '—'}
                </span>
                <span className="ct-kpi-v2-label"><Tooltip text="Uptrend when 6-month rolling avg price > 12-month avg. Downtrend when below.">Market Trend{variantLabel}</Tooltip></span>
            </div>
            <div className="ct-kpi-v2">
                <span className="ct-kpi-v2-value">{formatCurrency(effectiveKpis.rolling_3m_avg)}</span>
                <span className="ct-kpi-v2-label"><Tooltip text="Trailing 3-month average sale price (as of latest data month)">3M Avg{variantLabel}</Tooltip></span>
            </div>
            <div className="ct-kpi-v2">
                <span className="ct-kpi-v2-value">{formatCurrency(effectiveKpis.rolling_6m_avg)}</span>
                <span className="ct-kpi-v2-label"><Tooltip text="Trailing 6-month average sale price (as of latest data month)">6M Avg{variantLabel}</Tooltip></span>
            </div>
            <div className="ct-kpi-v2">
                <span className="ct-kpi-v2-value">{formatCurrency(effectiveKpis.rolling_12m_avg)}</span>
                <span className="ct-kpi-v2-label"><Tooltip text="Trailing 12-month average sale price (as of latest data month)">12M Avg{variantLabel}</Tooltip></span>
            </div>
        </div>
    );
}

/* ─── AI Trend Analysis Card ─────────────────────────────────────────────── */
function TrendAnalysisCard() {
    // Fetch on mount, Refresh, auto-expand after a load (hooks/useTrendAnalysis).
    const {
        analysis, loading, error, collapsed, setCollapsed, lines, refresh,
    } = useTrendAnalysis();

    if (loading && !analysis) {
        return (
            <div className="ct-analysis-card">
                <div className="ct-analysis-header">
                    <span className="ct-analysis-title">AI Trend Analysis</span>
                </div>
                <div className="ct-analysis-loading">Analyzing moving average crossovers...</div>
            </div>
        );
    }

    if (error && !analysis) return null;
    if (!analysis) return null;

    const firstLines = lines.slice(0, 2);

    return (
        <div className="ct-analysis-card">
            <div className="ct-analysis-header">
                <span className="ct-analysis-title">AI Trend Analysis</span>
                <div className="ct-analysis-actions">
                    {analysis.generated_at && (
                        <span className="ct-analysis-date">
                            {new Date(analysis.generated_at).toLocaleDateString()}
                        </span>
                    )}
                    <button
                        className="ct-analysis-refresh"
                        onClick={refresh}
                        disabled={loading}
                    >
                        {loading ? 'Analyzing...' : 'Refresh'}
                    </button>
                    <button
                        className="ct-analysis-toggle"
                        onClick={() => setCollapsed(c => !c)}
                    >
                        {collapsed ? 'Expand' : 'Collapse'}
                    </button>
                </div>
            </div>
            <div className="ct-analysis-body">
                {collapsed ? (
                    <p className="ct-analysis-text ct-analysis-preview">
                        {firstLines.join(' ')}
                        {lines.length > 2 && <span className="ct-analysis-more" onClick={() => setCollapsed(false)}> ...show more</span>}
                    </p>
                ) : (
                    lines.map((line, i) => (
                        <p key={i} className="ct-analysis-text">{line}</p>
                    ))
                )}
            </div>
        </div>
    );
}

/* ─── Variant Trends Table ───────────────────────────────────────────────── */
function VariantTrendsTable({ variantTrends, selectedVariant, onSelectVariant }) {
    const [expandedVariants, setExpandedVariants] = useState(new Set());
    const [groupFilter, setGroupFilter] = useState('All');

    const allVariants = useMemo(() => (variantTrends || []).map(v => v.variant), [variantTrends]);

    const filteredVariants = useMemo(() => {
        if (!variantTrends) return [];
        if (groupFilter === 'All') return variantTrends;
        return variantTrends.filter(v => v.variant === groupFilter);
    }, [variantTrends, groupFilter]);

    const toggleExpand = (variant, e) => {
        e.stopPropagation();
        setExpandedVariants(prev => {
            const next = new Set(prev);
            if (next.has(variant)) {
                next.delete(variant);
            } else {
                next.add(variant);
            }
            return next;
        });
    };

    const handleRowClick = (variant) => {
        onSelectVariant(selectedVariant === variant ? null : variant);
    };

    // Compute total row trend: downtrend if majority are downtrend
    const totalTrend = useMemo(() => totalTrendOf(variantTrends), [variantTrends]);

    if (!variantTrends || variantTrends.length === 0) {
        return (
            <div className="ct-trend-left">
                <div className="ct-trends-table-header">
                    <span className="ct-trends-table-title">Variant Trends</span>
                </div>
                <div style={{ padding: '1.5rem 1rem', color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center' }}>
                    No variant data available
                </div>
            </div>
        );
    }

    return (
        <div className="ct-trend-left">
            <div className="ct-trends-table-header">
                <span className="ct-trends-table-title">Variant Trends</span>
                <select
                    className="ct-variant-select"
                    value={groupFilter}
                    onChange={e => setGroupFilter(e.target.value)}
                >
                    <option value="All">Group ▾</option>
                    {allVariants.map(v => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>
            </div>
            <div className="ct-trends-col-head">
                <span><Tooltip text="Equipment variant grouping (e.g. TRA = Tractors, CMB = Combines)">Variant</Tooltip></span>
                <span><Tooltip text="Market direction based on 6M vs 12M rolling average sale price per variant">Trend</Tooltip></span>
            </div>

            {filteredVariants.map(variantRow => {
                const isExpanded = expandedVariants.has(variantRow.variant);
                const isSelected = selectedVariant === variantRow.variant;
                const hasSubs = variantRow.subcategories && variantRow.subcategories.length > 0;

                return (
                    <React.Fragment key={variantRow.variant}>
                        <div
                            className={`ct-variant-row${isSelected ? ' selected' : ''}`}
                            onClick={() => handleRowClick(variantRow.variant)}
                        >
                            <span className="ct-expand-btn" onClick={hasSubs ? (e) => toggleExpand(variantRow.variant, e) : undefined}>
                                {hasSubs ? (isExpanded ? '▼' : '▶') : ''}
                            </span>
                            <span className="ct-variant-name">{variantRow.variant}</span>
                            <TrendBadge trend={variantRow.market_trend} />
                        </div>

                        {isExpanded && hasSubs && variantRow.subcategories.map(sub => (
                            <div key={sub.category} className="ct-subcat-row">
                                <span>
                                    <span style={{ display: 'block' }}>{sub.category}</span>
                                    {sub.mf_category && (
                                        <Tooltip text="Listing category">
                                            <span className="ct-mf-badge">{sub.mf_category}</span>
                                        </Tooltip>
                                    )}
                                </span>
                                <TrendBadge trend={sub.market_trend} />
                            </div>
                        ))}
                    </React.Fragment>
                );
            })}

            {/* Total row */}
            <div className="ct-variant-row ct-trend-total" style={{ cursor: 'default' }}>
                <span className="ct-expand-btn" />
                <span className="ct-variant-name">Total</span>
                <TrendBadge trend={totalTrend} />
            </div>
        </div>
    );
}

/* ─── Rolling Avg Price Chart ────────────────────────────────────────────── */
function RollingAvgChart({ data, variantLabel }) {
    // Data is pre-filtered to the last complete year (Jan-Dec), sorted by month
    const sorted = (data || []).slice().sort((a, b) => a.month - b.month);
    const labels = sorted.map(d => d.month);

    const data3m = sorted.map(d => d.avg_3m);
    const data6m = sorted.map(d => d.avg_6m);
    const data12m = sorted.map(d => d.avg_12m);

    // Subtitle: last avg_3m vs last avg_12m (utils/categoryTrends)
    const rolling = rollingTrend(data);
    const isUptrend = rolling === 'Uptrend';
    const trendLabel = isUptrend ? '\u25b2 Uptrend' : '\u25bc Downtrend';
    const trendColor = isUptrend ? '#15803d' : '#b91c1c';

    const chartData = {
        labels,
        // Legend order matches PBI: 3M, 6M, 12M
        datasets: [
            {
                label: '3M Avg Net Sale Price',
                data: data3m,
                borderColor: '#1d4ed8',
                backgroundColor: 'transparent',
                borderWidth: 2.5,
                tension: 0,
                pointRadius: 0,
                pointHoverRadius: 4,
            },
            {
                label: '6M Avg Net Sale Price',
                data: data6m,
                borderColor: '#b45309',
                backgroundColor: 'transparent',
                borderWidth: 2.5,
                tension: 0,
                pointRadius: 0,
                pointHoverRadius: 4,
            },
            {
                label: '12M Avg Net Sale Price',
                data: data12m,
                borderColor: '#be185d',
                backgroundColor: 'transparent',
                borderWidth: 2.5,
                tension: 0,
                pointRadius: 0,
                pointHoverRadius: 4,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: { size: 11 },
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 16,
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${ctx.raw != null ? Math.round(ctx.raw / 1000) + 'K' : '—'}`,
                },
            },
        },
        scales: {
            y: {
                grid: { display: false },
                ticks: {
                    callback: (v) => `${Math.round(v / 1000)}K`,
                    font: { size: 11 },
                },
            },
            x: {
                grid: { display: false },
                title: { display: true, text: 'Month', font: { size: 11 } },
                ticks: { font: { size: 11 } },
            },
        },
    };

    return (
        <div className="ct-trend-chart-main">
            <div className="ct-trend-chart-title">
                <Tooltip text="Average net sale price over trailing 3, 6, and 12 month windows. When the 3M line is above the 12M line, prices are rising (uptrend). When below, prices are falling (downtrend).">
                    Rolling Avg. Net Sale Price{variantLabel ? ` — ${variantLabel}` : ''}
                </Tooltip>
            </div>
            {rolling !== null && (
                <div style={{ fontSize: 11, marginBottom: 4, color: trendColor, fontWeight: 600 }}>
                    {trendLabel}
                </div>
            )}
            <Line data={chartData} options={options} />
        </div>
    );
}

/* ─── YoY Price % Bar Chart ──────────────────────────────────────────────── */
function YoyPriceChart({ data }) {
    // Sort by 6M YoY ascending (worst performers at top of horizontal chart)
    const sorted = (data || [])
        .filter(d => d.pct_3m != null || d.pct_6m != null || d.pct_12m != null)
        .slice()
        .sort((a, b) => (a.pct_6m ?? 0) - (b.pct_6m ?? 0));

    const labels = sorted.map(d => d.variant);

    const chartData = {
        labels,
        datasets: [
            {
                label: '3M Avg YoY %',
                data: sorted.map(d => d.pct_3m ?? null),
                backgroundColor: '#1d4ed8',
                borderRadius: 3,
            },
            {
                label: '6M Avg YoY %',
                data: sorted.map(d => d.pct_6m ?? null),
                backgroundColor: '#b45309',
                borderRadius: 3,
            },
            {
                label: '12M Avg YoY %',
                data: sorted.map(d => d.pct_12m ?? null),
                backgroundColor: '#be185d',
                borderRadius: 3,
            },
        ],
    };

    const options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: { position: 'top', labels: { font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${ctx.raw != null ? (ctx.raw >= 0 ? '+' : '') + ctx.raw.toFixed(1) + '%' : '—'}`,
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    callback: (v) => `${v}%`,
                    font: { size: 11 },
                },
                grid: {
                    color: (ctx) => ctx.tick.value === 0 ? '#374151' : '#e2e8f0',
                    lineWidth: (ctx) => ctx.tick.value === 0 ? 2 : 1,
                },
            },
            y: { ticks: { font: { size: 11 } } },
        },
    };

    if (!sorted.length) return (
        <div className="ct-trend-chart-bar">
            <div className="ct-trend-chart-title">
                <Tooltip text="Year-over-year average sale price change by equipment variant. Shows which equipment types are gaining or losing value compared to last year.">
                    YoY Avg Price % by Variant
                </Tooltip>
            </div>
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No data</div>
        </div>
    );

    return (
        <div className="ct-trend-chart-bar">
            <div className="ct-trend-chart-title">
                <Tooltip text="Year-over-year average sale price change by equipment variant. Shows which equipment types are gaining or losing value compared to last year.">
                    YoY Avg Price % by Variant
                </Tooltip>
            </div>
            <Bar data={chartData} options={options} />
        </div>
    );
}

/* ─── Rolling Avg Price YoY % (3M / 6M / 12M) ───────────────────────────── */
function RollingAvgYoyByYearChart({ kpis }) {
    const values = [
        kpis?.rolling_3m_yoy ?? null,
        kpis?.rolling_6m_yoy ?? null,
        kpis?.rolling_12m_yoy ?? null,
    ];

    const chartData = {
        labels: ['3M', '6M', '12M'],
        datasets: [{
            label: 'Avg Net Sale Price YoY %',
            data: values,
            backgroundColor: ['#1d4ed8', '#b45309', '#be185d'],
            borderRadius: 4,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${ctx.raw != null ? (ctx.raw >= 0 ? '+' : '') + ctx.raw.toFixed(1) + '%' : '—'}`,
                },
            },
        },
        scales: {
            y: {
                ticks: { callback: (v) => `${v}%`, font: { size: 11 } },
                grid: {
                    color: (ctx) => ctx.tick.value === 0 ? '#374151' : '#e2e8f0',
                    lineWidth: (ctx) => ctx.tick.value === 0 ? 2 : 1,
                },
            },
            x: { ticks: { font: { size: 12, weight: '600' } } },
        },
    };

    return (
        <div className="ct-trend-chart-bar">
            <div className="ct-trend-chart-title">
                <Tooltip text="Year-over-year percentage change in rolling average sale prices. Positive = prices rising vs last year. Negative = prices falling.">
                    Rolling Avg. Price YoY %
                </Tooltip>
            </div>
            <Bar data={chartData} options={options} />
        </div>
    );
}

/* ─── Total Sales by Year Chart ──────────────────────────────────────────── */
const YEAR_COLORS = {
    2024: '#1d4ed8',
    2025: '#b45309',
    2026: '#be185d',
};

function TotalSalesChart({ data, variantLabel }) {
    const [mode, setMode] = useState('value'); // 'value' | 'units'
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const allYears = (data || []).map(e => e.year);
    const currentYear = allYears.length > 0 ? Math.max(...allYears) : null;

    const lastNonNull = (arr) => {
        for (let i = arr.length - 1; i >= 0; i--) {
            if (arr[i] != null) return i;
        }
        return -1;
    };

    const datasets = (data || []).map(yearEntry => {
        const isCurrentYear = yearEntry.year === currentYear;
        const values = mode === 'units'
            ? (yearEntry.monthly_units || []).map(v => (v == null ? undefined : v))
            : (yearEntry.monthly_sales || []).map(v => (v == null ? undefined : v));
        const lastIdx = isCurrentYear ? lastNonNull(values) : -1;
        const pointRadius = isCurrentYear
            ? values.map((_, i) => (i === lastIdx ? 5 : 2))
            : 2;

        return {
            label: String(yearEntry.year),
            data: values,
            borderColor: YEAR_COLORS[yearEntry.year] || '#64748b',
            backgroundColor: 'transparent',
            fill: false,
            tension: 0.3,
            pointRadius,
            borderWidth: isCurrentYear ? 2.5 : 1.5,
            spanGaps: false,
        };
    });

    const chartData = { labels: months, datasets };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: { size: 11 },
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 16,
                },
            },
            tooltip: {
                callbacks: {
                    label: mode === 'units'
                        ? (ctx) => `${ctx.dataset.label}: ${ctx.raw != null ? ctx.raw.toLocaleString('en-US') : '—'} units`
                        : (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`,
                },
            },
        },
        scales: {
            y: {
                grid: { display: true, color: 'rgba(0,0,0,0.06)' },
                ticks: {
                    callback: mode === 'units'
                        ? (v) => v.toLocaleString('en-US')
                        : (v) => `$${(v / 1_000_000).toFixed(1)}M`,
                    font: { size: 11 },
                },
            },
            x: {
                grid: { display: true, color: 'rgba(0,0,0,0.06)' },
                ticks: { font: { size: 11 } },
            },
        },
    };

    return (
        <div className="ct-trend-chart-bottom">
            <div className="ct-trend-chart-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tooltip text={mode === 'units'
                    ? "Number of units sold per month, compared across years. Shows volume trends independent of price changes."
                    : "Total net sales value (sale price minus overallowance) per month, compared across years. Shows seasonal patterns and year-over-year volume trends."
                }>
                    {mode === 'units' ? 'Monthly Units Sold by Year' : 'Monthly Sales Value by Year ($)'}{variantLabel ? ` — ${variantLabel}` : ''}
                </Tooltip>
                <div className="ct-sales-toggle">
                    <button className={`ct-sales-toggle-btn ${mode === 'value' ? 'active' : ''}`} onClick={() => setMode('value')}>Value ($)</button>
                    <button className={`ct-sales-toggle-btn ${mode === 'units' ? 'active' : ''}`} onClick={() => setMode('units')}>Units</button>
                </div>
            </div>
            <Line data={chartData} options={options} />
        </div>
    );
}

/* ─── Trend Sub-Tab ──────────────────────────────────────────────────────── */
function TrendSubTab({ trendsData, trendsLoading, trendsError, dateFrom, dateTo, onDateChange }) {
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [localFrom, setLocalFrom] = useState(dateFrom || '');
    const [localTo, setLocalTo] = useState(dateTo || '');

    const handleApply = () => {
        if (onDateChange) onDateChange(localFrom, localTo);
    };
    const handleClear = () => {
        setLocalFrom('');
        setLocalTo('');
        if (onDateChange) onDateChange('', '');
    };

    const isFiltered = !!(dateFrom || dateTo);

    const dateBar = (
        <div className="ct-date-bar">
            <span className="ct-date-label">
                <Tooltip text="Filter sold data to a specific date range. Affects KPIs, charts and variant trends.">Analysis Window</Tooltip>
            </span>
            <input
                type="date"
                className="ct-date-input"
                value={localFrom}
                onChange={e => setLocalFrom(e.target.value)}
                placeholder="From"
            />
            <span className="ct-date-sep">→</span>
            <input
                type="date"
                className="ct-date-input"
                value={localTo}
                onChange={e => setLocalTo(e.target.value)}
                placeholder="To"
            />
            <button className="ct-date-apply" onClick={handleApply}>Apply</button>
            {isFiltered && (
                <button className="ct-date-clear" onClick={handleClear}>Clear</button>
            )}
            {isFiltered && (
                <span className="ct-date-notice">
                    Showing {dateFrom || '…'} → {dateTo || 'today'}
                </span>
            )}
        </div>
    );

    const {
        variant_trends = [],
        rolling_avg_by_month = [],
        sales_by_year_month = [],
        yoy_price_by_variant = [],
        current_year = new Date().getFullYear(),
        sales_by_year_month_by_variant = {},
        rolling_avg_by_month_by_variant = {},
    } = trendsData || {};

    // Variant-filtered KPIs when a variant is selected (utils/categoryTrends)
    const effectiveKpis = useMemo(
        () => computeEffectiveKpis(trendsData, selectedVariant),
        [trendsData, selectedVariant],
    );

    if (trendsLoading) {
        return <div>{dateBar}<div className="bo-loading"><div className="bo-spinner" />Loading trend data...</div></div>;
    }
    if (trendsError) {
        return <div>{dateBar}<div className="bo-error">{trendsError}</div></div>;
    }
    if (!trendsData) {
        return <div>{dateBar}<div className="bo-loading">No data loaded yet.</div></div>;
    }

    const hasVariantData = variant_trends && variant_trends.length > 0;

    // Filter rolling avg to the last complete year (current_year - 1 = 2025) to match PBI chart range
    const lastCompleteYear = lastCompleteYearOf({ current_year });

    const filteredRollingAvg = (selectedVariant
        ? (rolling_avg_by_month_by_variant[selectedVariant] || [])
        : (rolling_avg_by_month || [])
    ).filter(d => d.year === lastCompleteYear);
    const filteredSalesByYear = selectedVariant
        ? (sales_by_year_month_by_variant[selectedVariant] || [])
        : (sales_by_year_month || []);
    const filteredYoyPrice = selectedVariant
        ? (yoy_price_by_variant || []).filter(d => d.variant === selectedVariant)
        : (yoy_price_by_variant || []);

    return (
        <div>
            {dateBar}
            <TrendKpiStrip kpis={effectiveKpis} selectedVariant={selectedVariant} />
            <TrendAnalysisCard />

            {!hasVariantData ? (
                <div className="ct-trend-body">
                    <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        No variant data available
                    </div>
                </div>
            ) : (
                <div className="ct-trend-body">
                    <VariantTrendsTable
                        variantTrends={variant_trends}
                        selectedVariant={selectedVariant}
                        onSelectVariant={setSelectedVariant}
                    />

                    <div className="ct-trend-right">
                        <div className="ct-trend-charts-top">
                            <RollingAvgChart data={filteredRollingAvg} variantLabel={selectedVariant} />
                            <RollingAvgYoyByYearChart kpis={effectiveKpis} />
                        </div>
                        <TotalSalesChart data={filteredSalesByYear} variantLabel={selectedVariant} />
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Category Analysis Table (for Market tab) — uses variant_trends for consistency ── */
function CategoryAnalysisTable({ trendsData }) {
    // The dealership's last aging boundary and critical days (180 / 90 by default).
    const { aging } = useDealershipSettings();
    const oldest = aging.boundaries[2];
    const critical = aging.criticalDays;
    const rows = useMemo(() => {
        const vt = trendsData?.variant_trends || [];
        return vt.map(v => {
            const subs = v.subcategories || [];
            const totalStock = v.in_stock || 0;
            const totalCritical = subs.reduce((s, c) => s + (c.critical || 0), 0);
            const totalTq = subs.reduce((s, c) => s + (c.tq_sold || 0), 0);
            const totalLq = subs.reduce((s, c) => s + (c.lq_sold || 0), 0);
            const totalLyq = subs.reduce((s, c) => s + (c.lyq_sold || 0), 0);
            // Weighted avg days from subcategories
            const daysSum = subs.reduce((s, c) => s + ((c.avg_days || 0) * (c.in_stock || 0)), 0);
            const avgDays = totalStock > 0 ? Math.round(daysSum / totalStock) : null;
            const yoyPct = totalLyq > 0 ? Math.round(((totalTq - totalLyq) / totalLyq) * 1000) / 10 : null;
            const qoqPct = totalLq > 0 ? Math.round(((totalTq - totalLq) / totalLq) * 1000) / 10 : null;
            const severity = ((avgDays || 0) > oldest || (yoyPct || 0) < -30) ? 4
                : ((avgDays || 0) > critical || (yoyPct || 0) < -10) ? 2 : 0;
            return {
                category: v.variant,
                in_stock: totalStock,
                avg_days: avgDays,
                critical: totalCritical,
                tq_sold: totalTq,
                lq_sold: totalLq,
                lyq_sold: totalLyq,
                yoy_pct: yoyPct,
                qoq_pct: qoqPct,
                severity,
            };
        }).sort((a, b) => (b.in_stock || 0) - (a.in_stock || 0));
    }, [trendsData, oldest, critical]);

    const q = trendsData?.current_quarter || '';
    const yr = trendsData?.current_year || '';
    const prevQ = q > 1 ? q - 1 : 4;
    const prevQYear = q > 1 ? yr : yr - 1;

    if (!rows.length) {
        return (
            <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                No category data available
            </div>
        );
    }

    return (
        <div className="ct-market-section">
            <div className="ct-section-header">
                <h3>Category Analysis</h3>
                <span className="ct-section-sub">Inventory health and sales velocity by equipment variant</span>
            </div>
            <div className="ct-table-card">
                <table className="ct-table">
                    <thead>
                        <tr>
                            <th>Variant</th>
                            <th className="ct-r">
                                <Tooltip text="Active units currently in stock">In Stock</Tooltip>
                            </th>
                            <th className="ct-r">
                                <Tooltip text="Weighted average days in stock across all units">Avg Days</Tooltip>
                            </th>
                            <th className="ct-r">
                                <Tooltip text={`Units in stock longer than ${critical} days`}>Critical</Tooltip>
                            </th>
                            <th className="ct-r">
                                <Tooltip text={`Units sold Q${q} ${yr} vs Q${q} ${yr - 1}`}>Sold YoY</Tooltip>
                            </th>
                            <th className="ct-r">YoY %</th>
                            <th className="ct-r">
                                <Tooltip text={`Units sold Q${q} ${yr} vs Q${prevQ} ${prevQYear}`}>Sold QoQ</Tooltip>
                            </th>
                            <th className="ct-r">QoQ %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.category}>
                                <td>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <Tooltip text={
                                            row.severity >= 4 ? `High risk: avg days >${oldest} or YoY decline >30%`
                                            : row.severity >= 2 ? 'Moderate risk: avg days >90 or YoY decline >10%'
                                            : 'Low risk: inventory and sales trending normally'
                                        }>
                                            <span
                                                className="ct-cat-dot"
                                                style={{
                                                    backgroundColor: row.severity >= 4 ? '#b91c1c'
                                                        : row.severity >= 2 ? '#b45309'
                                                            : '#15803d'
                                                }}
                                            />
                                        </Tooltip>
                                        <span className="ct-cat-name">{row.category}</span>
                                    </span>
                                </td>
                                <td className="ct-r">{row.in_stock ?? '—'}</td>
                                <td className="ct-r">
                                    {row.avg_days != null
                                        ? <span style={{
                                            color: row.avg_days > oldest ? '#b91c1c'
                                                : row.avg_days > critical ? '#b45309'
                                                    : 'inherit',
                                            fontWeight: row.avg_days > critical ? 600 : 400,
                                        }}>{row.avg_days}d</span>
                                        : <span className="ct-muted">—</span>}
                                </td>
                                <td className="ct-r">
                                    {row.critical > 0
                                        ? <span className="ct-badge ct-badge-bad">{row.critical}</span>
                                        : <span className="ct-muted">—</span>}
                                </td>
                                <td className="ct-r">
                                    <span>{row.tq_sold}</span>{' '}
                                    <span className="ct-muted">vs {row.lyq_sold}</span>
                                </td>
                                <td className="ct-r"><Delta value={row.yoy_pct} /></td>
                                <td className="ct-r">
                                    <span>{row.tq_sold}</span>{' '}
                                    <span className="ct-muted">vs {row.lq_sold}</span>
                                </td>
                                <td className="ct-r"><Delta value={row.qoq_pct} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─── Market Signals Table (for Market tab) ──────────────────────────────── */
function MarketSignalsTable({ trendsData }) {
    const { q, yr, prevQ, prevQYear } = quarterLabels(trendsData);

    if (!trendsData?.market_signals?.length) {
        return (
            <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                No market signal data available.
            </div>
        );
    }

    return (
        <div className="ct-market-section">
            <div className="ct-section-header">
                <h3>Market Signals</h3>
                <span className="ct-section-sub">Is the overall market down, or just this category?</span>
            </div>
            <div className="ct-table-card">
                <table className="ct-table">
                    <thead>
                        <tr>
                            <th>Signal</th>
                            <th className="ct-r">Current</th>
                            <th className="ct-r">Q{q} {yr}</th>
                            <th className="ct-r">Q{q} {yr - 1}</th>
                            <th className="ct-r">YoY</th>
                            <th className="ct-r">Q{prevQ} {prevQYear}</th>
                            <th className="ct-r">QoQ</th>
                            <th className="ct-r">12m Trend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trendsData.market_signals.map(sig => {
                            const fmt = (v) => formatSignalValue(sig.key, v);
                            const sparkData = (trendsData.market_context || []).map(m => m[sig.key]).filter(v => v != null);
                            return (
                                <tr key={sig.key}>
                                    <td><span className="ct-cat-name">{sig.label}</span></td>
                                    <td className="ct-r ct-mono">{fmt(sig.current)}</td>
                                    <td className="ct-r ct-mono">{fmt(sig.current_q_avg)}</td>
                                    <td className="ct-r ct-muted">{fmt(sig.last_year_q_avg)}</td>
                                    <td className="ct-r"><Delta value={sig.yoy_pct} /></td>
                                    <td className="ct-r ct-muted">{fmt(sig.prev_q_avg)}</td>
                                    <td className="ct-r"><Delta value={sig.qoq_pct} /></td>
                                    <td className="ct-r"><Sparkline data={sparkData} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ─── Risk Sub-Tab — VARIANT → CATEGORY expandable hierarchy ─────────────── */
function RiskSubTab({ trendsData, trendsLoading, trendsError }) {
    const { aging } = useDealershipSettings();
    const oldest = aging.boundaries[2];
    const critical = aging.criticalDays;
    const [activePresets, setActivePresets] = useState(new Set([0]));
    const [yoyMin, setYoyMin] = useState('');
    const [yoyMax, setYoyMax] = useState('');
    const [qoqMin, setQoqMin] = useState('');
    const [qoqMax, setQoqMax] = useState('');
    const [expandedVariants, setExpandedVariants] = useState(new Set());

    const allVariants = trendsData?.variant_trends || [];
    const { q, yr, prevQ, prevQYear } = quarterLabels(trendsData);

    // Preset + custom filter at subcategory level; a VARIANT row shows if ANY
    // of its subcategories match (utils/categoryTrends).
    const custom = { yoyMin, yoyMax, qoqMin, qoqMax };
    const {
        variants: filteredVariants, hasFilter, subsFor,
    } = filterRiskVariants(allVariants, activePresets, custom);

    // Auto-expand all variants when a filter is active so matching sub-rows are visible
    const visibleExpanded = hasFilter ? new Set(filteredVariants.map(v => v.variant)) : expandedVariants;

    const togglePreset = (idx) => setActivePresets(prev => nextPresets(prev, idx));

    const clearAll = () => {
        setActivePresets(new Set([0]));
        setYoyMin(''); setYoyMax(''); setQoqMin(''); setQoqMax('');
    };

    const toggleExpand = (variant) => {
        setExpandedVariants(prev => {
            const next = new Set(prev);
            if (next.has(variant)) next.delete(variant);
            else next.add(variant);
            return next;
        });
    };

    // KPI strip aggregated at variant level (not subcategory)
    const kpis = riskKpis(allVariants);

    return (
        <div>
            {/* ── Summary KPIs ── */}
            {kpis && (
                <div className="ct-kpi-strip">
                    <div className="ct-kpi">
                        <span className="ct-kpi-value">{kpis.totalVariants}</span>
                        <span className="ct-kpi-label"><Tooltip text="Distinct equipment variants in current active inventory">Variants</Tooltip></span>
                    </div>
                    <div className="ct-kpi">
                        <span className="ct-kpi-value">{kpis.totalUnits.toLocaleString('en-US')}</span>
                        <span className="ct-kpi-label"><Tooltip text="Total active units across all variants and categories">Units in Stock</Tooltip></span>
                    </div>
                    <div className="ct-kpi ct-kpi-bad">
                        <span className="ct-kpi-value">{kpis.declining}</span>
                        <span className="ct-kpi-label">
                            <Tooltip text="Categories where YoY sold count dropped more than 5%">
                                Declining YoY
                            </Tooltip>
                        </span>
                    </div>
                    <div className="ct-kpi ct-kpi-good">
                        <span className="ct-kpi-value">{kpis.growing}</span>
                        <span className="ct-kpi-label"><Tooltip text="Categories where sold count grew more than 5% vs same quarter last year">Growing YoY</Tooltip></span>
                    </div>
                    <div className="ct-kpi ct-kpi-bad">
                        <span className="ct-kpi-value">{kpis.totalCritical}</span>
                        <span className="ct-kpi-label">
                            <Tooltip text={`Units sitting in stock for more than ${critical} days`}>
                                Critical &gt;{critical}d
                            </Tooltip>
                        </span>
                    </div>
                    {kpis.worst && (
                        <div className="ct-kpi ct-kpi-alert">
                            <span className="ct-kpi-value">{Math.round(kpis.worst.yoy_pct)}%</span>
                            <span className="ct-kpi-label"><Tooltip text="Variant with the largest year-over-year sold decline">Worst: {kpis.worst.variant}</Tooltip></span>
                        </div>
                    )}
                </div>
            )}

            {/* ── Loading / Error ── */}
            {trendsLoading && <div className="bo-loading"><div className="bo-spinner" />Loading category trends...</div>}
            {trendsError && <div className="bo-error">{trendsError}</div>}

            {/* ── Preset Filter Pills ── */}
            <div className="ct-controls">
                <div className="ct-presets">
                    {PRESETS.map((p, i) => (
                        <div key={p.label} className="ct-preset-wrap">
                            <button
                                className={`ct-preset ${activePresets.has(i) ? 'ct-preset-active' : ''}`}
                                onClick={() => togglePreset(i)}
                            >
                                {p.label}
                            </button>
                            <div className="ct-preset-tooltip">{p.tip}</div>
                        </div>
                    ))}
                </div>

                {/* Custom Range */}
                <div className="ct-range-row">
                    <div className="ct-range-group">
                        <span className="ct-range-label">YoY %</span>
                        <input type="number" className="ct-range-input" placeholder="Min" value={yoyMin}
                            onChange={e => setYoyMin(e.target.value)} />
                        <span className="ct-range-to">–</span>
                        <input type="number" className="ct-range-input" placeholder="Max" value={yoyMax}
                            onChange={e => setYoyMax(e.target.value)} />
                    </div>
                    <div className="ct-range-group">
                        <span className="ct-range-label">QoQ %</span>
                        <input type="number" className="ct-range-input" placeholder="Min" value={qoqMin}
                            onChange={e => setQoqMin(e.target.value)} />
                        <span className="ct-range-to">–</span>
                        <input type="number" className="ct-range-input" placeholder="Max" value={qoqMax}
                            onChange={e => setQoqMax(e.target.value)} />
                    </div>
                    {hasFilter && (
                        <span className="ct-range-meta">
                            {filteredVariants.length} of {allVariants.length} variants
                            <button className="ct-range-clear" onClick={clearAll}>Clear all</button>
                        </span>
                    )}
                </div>
            </div>

            {/* ── Variant → Category Hierarchy Table ── */}
            {!trendsLoading && !trendsError && (
                <div className="ct-table-card">
                    {/* Column header */}
                    <div className="ct-risk-col-head">
                        <span />
                        <span>Variant / Category</span>
                        <span className="ct-r">
                            <Tooltip text="Active units currently in stock">Stock</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text="Average days this category has been in stock">Avg Days</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text={`Units in stock longer than ${critical} days`}>Critical</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text={`Year-over-year % change in sold units — Q${q} ${yr} vs Q${q} ${yr - 1}`}>Sold YoY</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text="Year-over-year % change in sold units">YoY %</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text={`Units sold Q${q} ${yr} vs Q${prevQ} ${prevQYear}`}>Sold QoQ</Tooltip>
                        </span>
                        <span className="ct-r">
                            <Tooltip text="Quarter-over-quarter % change in sold units">QoQ %</Tooltip>
                        </span>
                    </div>

                    {filteredVariants.length === 0 && (
                        <div style={{ padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
                            No variants match the current filter.
                        </div>
                    )}

                    {filteredVariants.map(vRow => {
                        const isExpanded = visibleExpanded.has(vRow.variant);
                        // When a filter is active, only show subcategories that match
                        const subs = subsFor(vRow);
                        const hasSubs = subs.length > 0;
                        // Aggregate variant-level totals from subcategories
                        const {
                            stock: totalStock, avgDays, critical: totalCritical, tq: totalTq,
                            lyq: totalLyq, lq: totalLq, yoy: varYoy, qoq: varQoq,
                        } = aggregateVariantRow(vRow, subs);

                        return (
                            <React.Fragment key={vRow.variant}>
                                {/* VARIANT row */}
                                <div
                                    className="ct-risk-variant-row"
                                    onClick={() => hasSubs && toggleExpand(vRow.variant)}
                                    style={{ cursor: hasSubs ? 'pointer' : 'default' }}
                                >
                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                        {hasSubs ? (isExpanded ? '▼' : '▶') : ''}
                                    </span>
                                    <span>{vRow.variant}</span>
                                    <span className="ct-r">{totalStock || '—'}</span>
                                    <span className="ct-r">{avgDays != null ? `${avgDays}d` : '—'}</span>
                                    <span className="ct-r">
                                        {totalCritical > 0
                                            ? <span className="ct-badge ct-badge-bad">{totalCritical}</span>
                                            : <span className="ct-muted">—</span>}
                                    </span>
                                    <span className="ct-r">
                                        <span>{totalTq}</span>{' '}<span className="ct-muted">vs {totalLyq}</span>
                                    </span>
                                    <span className="ct-r">
                                        {varYoy != null ? <Delta value={varYoy} /> : <span className="ct-muted">—</span>}
                                    </span>
                                    <span className="ct-r">
                                        <span>{totalTq}</span>{' '}<span className="ct-muted">vs {totalLq}</span>
                                    </span>
                                    <span className="ct-r">
                                        {varQoq != null ? <Delta value={varQoq} /> : <span className="ct-muted">—</span>}
                                    </span>
                                </div>

                                {/* CATEGORY sub-rows */}
                                {isExpanded && hasSubs && subs.map(sub => (
                                    <div key={sub.category} className="ct-risk-subcat-row">
                                        <span>
                                            <span
                                                className="ct-cat-dot"
                                                style={{
                                                    backgroundColor: sub.severity >= 4 ? '#b91c1c'
                                                        : sub.severity >= 2 ? '#b45309'
                                                            : '#15803d'
                                                }}
                                            />
                                        </span>
                                        <span>
                                            <span style={{ display: 'block', fontSize: '0.78rem' }}>{sub.category}</span>
                                            {sub.mf_category && (
                                                <span className="ct-mf-badge">{sub.mf_category}</span>
                                            )}
                                        </span>
                                        <span className="ct-r">{sub.in_stock || '—'}</span>
                                        <span className="ct-r">
                                            {sub.avg_days != null
                                                ? <span style={{
                                                    color: sub.avg_days > oldest ? '#b91c1c'
                                                        : sub.avg_days > critical ? '#b45309'
                                                            : 'inherit',
                                                    fontWeight: sub.avg_days > critical ? 600 : 400,
                                                }}>{sub.avg_days}d</span>
                                                : '—'}
                                        </span>
                                        <span className="ct-r">
                                            {sub.critical > 0
                                                ? <span className="ct-badge ct-badge-bad">{sub.critical}</span>
                                                : <span className="ct-muted">—</span>}
                                        </span>
                                        <span className="ct-r">
                                            <span>{sub.tq_sold}</span>{' '}<span className="ct-muted">vs {sub.lyq_sold ?? '—'}</span>
                                        </span>
                                        <span className="ct-r"><Delta value={sub.yoy_pct} /></span>
                                        <span className="ct-r">
                                            <span>{sub.tq_sold}</span>{' '}<span className="ct-muted">vs {sub.lq_sold}</span>
                                        </span>
                                        <span className="ct-r"><Delta value={sub.qoq_pct} /></span>
                                    </div>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ─── Main Component ────────────────────────────────────────────────────── */
// Pricing and Risk were placeholders that rendered "In Development" to anyone who
// clicked them — a construction sign in a customer-facing console. Removed rather
// than hidden behind a flag: nothing was built behind either, and the risk content
// people actually want is already under Market, which renders RiskSubTab.
const SUB_TABS = [
    { id: 'trend', label: 'Trend' },
    { id: 'market', label: 'Market' },
];

export default function CategoryTrendsView({ trendsData, trendsLoading, trendsError, dateFrom, dateTo, onDateChange }) {
    const [activeSubTab, setActiveSubTab] = useState('trend');

    return (
        <div className="ct-view">
            {/* ── Sub-tab navigation ── */}
            <div className="ct-subtabs">
                {SUB_TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`ct-subtab${activeSubTab === tab.id ? ' active' : ''}`}
                        onClick={() => setActiveSubTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Trend tab ── */}
            {activeSubTab === 'trend' && (
                <TrendSubTab
                    trendsData={trendsData}
                    trendsLoading={trendsLoading}
                    trendsError={trendsError}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onDateChange={onDateChange}
                />
            )}

            {/* ── Market tab ── */}
            {activeSubTab === 'market' && (
                <div>
                    <MarketSignalsTable trendsData={trendsData} />
                    <RiskSubTab trendsData={trendsData} trendsLoading={trendsLoading} trendsError={trendsError} />
                </div>
            )}
        </div>
    );
}

CategoryTrendsView.propTypes = {
    trendsData: PropTypes.shape({
        rows: PropTypes.array,
        market_context: PropTypes.array,
        market_signals: PropTypes.array,
        current_quarter: PropTypes.number,
        current_year: PropTypes.number,
        variant_trends: PropTypes.array,
        kpis: PropTypes.object,
        rolling_avg_by_month: PropTypes.array,
        sales_by_year_month: PropTypes.array,
        yoy_price_by_variant: PropTypes.array,
    }),
    trendsLoading: PropTypes.bool,
    trendsError: PropTypes.string,
};

CategoryTrendsView.defaultProps = {
    trendsData: null,
    trendsLoading: false,
    trendsError: null,
};
